import { useQuery } from '@tanstack/react-query';
import { Wallet } from 'lucide-react';
import { useApi } from '@/hooks/useApi';
import { EmptyState } from '@/components/ui/empty-state';
import { Pill, toneFor } from '@/components/ui/pill';
import { TableSkeleton } from '@/components/ui/table-skeleton';
import {
  DataTable,
  DataTableHeader,
  DataTableHeaderCell,
  DataTableRow,
} from '@/components/ui/data-table';
import { PageHeader } from '@/components/ui/page-header';
import { formatMoney } from '@/lib/money';
import { formatDate } from '@/lib/dates';
import { payoutStatusText } from '@/lib/outreach-copy';
import { InfoHint } from '@/components/ui/InfoHint';
import type { OutreachPayout } from '@/api/types';

export function PayoutsPage(): JSX.Element {
  const api = useApi();
  const query = useQuery({
    queryKey: ['portal-payouts'],
    queryFn: () => api.get<OutreachPayout[]>('/portal/payouts'),
  });

  const count = query.data?.length ?? 0;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Payouts"
        description="Every payout made to you, one row per pay period, newest first."
      />

      <p className="text-xs uppercase tracking-wide text-muted-foreground">
        {query.isLoading ? 'Loading' : `${count} payout${count === 1 ? '' : 's'}`}
      </p>

      {query.isLoading ? (
        <TableSkeleton cols={['w-24', 'w-20', 'w-24', 'w-16']} />
      ) : !query.data || query.data.length === 0 ? (
        <EmptyState
          icon={<Wallet className="h-6 w-6" />}
          title="No payouts yet."
          hint="When an admin runs payroll for a pay period, your payout for it shows up here."
        />
      ) : (
        <DataTable>
          <DataTableHeader>
            <tr>
              <DataTableHeaderCell>
                <span className="inline-flex items-center gap-1">
                  Pay period starting
                  <InfoHint hintKey="payoutWeekOf" variant="inline" />
                </span>
              </DataTableHeaderCell>
              <DataTableHeaderCell>
                <span className="inline-flex items-center gap-1">
                  Status
                  <InfoHint hintKey="payoutStatus" variant="inline" />
                </span>
              </DataTableHeaderCell>
              <DataTableHeaderCell>Paid on</DataTableHeaderCell>
              <DataTableHeaderCell numeric>
                <span className="inline-flex items-center gap-1">
                  Amount
                  <InfoHint hintKey="payoutTotalAmount" variant="inline" />
                </span>
              </DataTableHeaderCell>
            </tr>
          </DataTableHeader>
          <tbody>
            {query.data.map((p) => (
              <DataTableRow key={p.id}>
                <td className="py-2" data-label="Pay period starting">
                  {formatPeriodStart(p.payrollWeekOf)}
                </td>
                <td className="py-2" data-label="Status">
                  <span className="inline-flex items-center gap-1.5">
                    <Pill tone={toneFor(p.status)}>{payoutStatusText(p.status)}</Pill>
                    {p.paidVia === 'RETAINER_OFFPLATFORM' && (
                      <Pill tone="muted">Manual / off-platform</Pill>
                    )}
                  </span>
                </td>
                <td className="py-2" data-label="Paid on">
                  {formatDate(p.paidAt)}
                </td>
                <td className="py-2 text-right tabular-nums" data-label="Amount">
                  ${formatMoney(p.totalAmount)}
                </td>
              </DataTableRow>
            ))}
          </tbody>
        </DataTable>
      )}
    </div>
  );
}

/**
 * The pay period's start is stored as a UTC calendar date. Read the date part
 * as-is so a viewer west of UTC doesn't see the day before.
 */
function formatPeriodStart(v: string | null | undefined): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(v ?? ''));
  if (!m) return formatDate(v);
  return formatDate(new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
}
